import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import moment from 'moment'
import { useExperimentApi } from '../../store/api/hooks/experimentApiHook'
import Text from '../../elements/Typo/Text'
import Icon from '../icons'
import { formatNumberWithCommas, isBuisenessOwnerRepectToExperiment, trackEvent } from '../../_common/functions'
import Button from '../../elements/button'
import { useUserSelector } from '../../store/selectors/userSelector'
import { useExperimentSelector } from '../../store/selectors/experimentSelector'

interface ExperimentCommentModalProps {
    _ref: any
    success?: () => void
    viewOnly?: boolean
    measurementModalRef?: any
    updateComments: (comments: any[]) => void
}

const ExperimentCommentModal: React.FC<ExperimentCommentModalProps> = ({ _ref, success = () => {}, viewOnly = false, measurementModalRef, updateComments = (data: any[]) => {} }) => {
    const { t } = useTranslation()
    const params: any = useParams()
    const experimentApi = useExperimentApi()
    const user: any = useUserSelector()
    const experiment = useExperimentSelector()

    const [comments, setComments] = useState<any[]>([])

    const [msg, setMsg] = useState<string>('')

    const createProfilePicture = (chatUser: any) => {
        return (
            <div className="text-[0.5rem] font-semibold bg-primary text-secondary w-[1.083rem] h-[1.083rem] rounded-full leading-none flex items-center justify-center">
                {chatUser?.first_name?.charAt(0)}
                {chatUser?.last_name?.charAt(0)}
            </div>
        )
    }

    const getUserName = (chatUser: any) => {
        return [chatUser.first_name, chatUser.last_name].join(' ')
    }

    const getComments = () => {
        experimentApi.getComments(
            { experiment_literal: params.experiment || params.exptLiteral },
            (resp: any) => {
                if (resp.data.length) {
                    const allComments = [...resp.data, ...resp.data[0].replies]
                    setComments(allComments)
                    trackEvent('experiment_review_comments_loaded', { experiment_literal: params.experiment || params.exptLiteral, comment_count: allComments.length })
                } else {
                    setComments([])
                    trackEvent('experiment_review_comments_loaded', { experiment_literal: params.experiment || params.exptLiteral, comment_count: 0 })
                }
            },
            () => {
                trackEvent('experiment_review_comments_load_failed', { experiment_literal: params.experiment || params.exptLiteral })
            },
        )
    }

    const renderUser = (chat: any) => {
        return (
            <div className="chat flex gap-3">
                {createProfilePicture(chat.user)}
                <div className="text flex-grow bg-[#f0f0f0] rounded-[0.3rem] p-2">
                    <div className="flex justify-between items-center">
                        <Text size="xl" className="font-semibold">
                            {getUserName(chat.user)}
                        </Text>
                        <Text size="xxs" className="italic">
                            {moment.utc(chat.created_at).local().format('MMM DD, YYYY ~ hh:mm A')}
                        </Text>
                    </div>
                    <Text size="xl">{chat.content}</Text>
                </div>
            </div>
        )
    }

    const renderMe = (chat: any) => {
        return (
            <div className="chat flex gap-3">
                <div className="text flex-grow bg-[#f0f0f0] rounded-[0.3rem] p-2">
                    <div className="flex justify-between items-center">
                        <Text size="xl" className="font-semibold">
                            {getUserName(chat.user)}
                        </Text>
                        <Text size="xxs" className="italic">
                            {moment.utc(chat.created_at).local().format('MMM DD, YYYY ~ hh:mm A')}
                        </Text>
                    </div>

                    <Text size="xl">{chat.content}</Text>
                </div>
                {createProfilePicture(chat.user)}
            </div>
        )
    }

    const postComment = () => {
        const payload: any = { content: msg }
        if (comments?.[0]?.id) {
            payload.parent_id = comments?.[0]?.id
        }
        trackEvent('experiment_review_modal_closed', { experiment_literal: params.experiment || params.exptLiteral })
        _ref?.current?.close()
        experimentApi.postComment(
            {
                experiment_literal: params.experiment || params.exptLiteral,
                post: payload,
            },
            (resp: any) => {
                setMsg('')
                success()
                getComments()
            },
            () => {
                trackEvent('experiment_review_submit_failed', { experiment_literal: params.experiment || params.exptLiteral })
                toast.error(t('Failed to submit your comment.'))
            },
        )
    }

    useEffect(() => {
        getComments()
    }, [])
    useEffect(() => {
        updateComments(comments)
    }, [comments])

    useEffect(() => {
        const dialog = _ref?.current
        if (!dialog) return
        const obs = new MutationObserver(() => {
            if (dialog.open) {
                const mode = isBuisenessOwnerRepectToExperiment(experiment, user?.user_literal) ? 'write' : 'view'
                trackEvent('experiment_review_modal_opened', { experiment_literal: params.experiment || params.exptLiteral, mode })
            }
        })
        obs.observe(dialog, { attributes: true, attributeFilter: ['open'] })
        return () => obs.disconnect()
    }, [])

    useEffect(() => {
        const channel = new BroadcastChannel('EXPERIMENT_REVIEW')
        const handleMessage = (event: MessageEvent) => {
            if (event.data.type === 'EXPERIMENT_REVIEW') {
                getComments()
            }
        }
        channel.addEventListener('message', handleMessage)

        return () => {
            channel.removeEventListener('message', handleMessage)
            channel.close()
        }
    }, [])

    return (
        <dialog ref={_ref} className="modal">
            <div className="modal-box w-[29.125rem] max-w-3xl rounded-[0.833rem] !p-0">
                <Text className="text-center text-[1.25rem] font-bold pt-4">{isBuisenessOwnerRepectToExperiment(experiment, user?.user_literal) ? t('Write Review') : t('View Review')}</Text>
                <Text className="text-center py-4 text-[1rem] font-medium">{t('Briefly describe what is being reviewed. Mention the purpose or context.')}</Text>
                <div className="p-4 pt-0">
                    <div className="chats flex flex-col gap-3">
                        {comments.map((comment: any) => (
                            <div key={'comment_' + comment.id}>{comment.user.email == user?.email ? renderMe(comment) : renderUser(comment)}</div>
                        ))}

                        <div className="chat flex gap-3">
                            <div className="text-[0.5rem] font-semibold bg-primary text-secondary w-[1.083rem] h-[1.083rem] rounded-full leading-none flex items-center justify-center">
                                {user?.first_name?.charAt(0)}
                                {user?.last_name?.charAt(0)}
                            </div>
                            <textarea
                                value={msg}
                                onChange={(e: any) => {
                                    if (e.target.value.length > 500) {
                                        setMsg(e.target.value.slice(0, 500))
                                    } else {
                                        setMsg(e.target.value)
                                    }
                                }}
                                className="flex-grow rounded-[0.25rem] border border-[#c1c1c1] outline-none text-[0.7rem] p-2"
                            ></textarea>
                        </div>
                        <div className="flex justify-end -mt-3 ">
                            <Text className={`text-[0.6rem] ${msg.length == 500 ? 'text-red-600 bg-red-200' : 'text-slate-600 bg-slate-200'} px-2 py-1 leading-none rounded-sm`}>{formatNumberWithCommas(msg.length)}/500</Text>
                        </div>
                    </div>

                    <div className="flex items-center justify-center mt-3 ">
                        <div className="flex items-center gap-3">
                            <Button
                                theme="secondary"
                                size="xs"
                                styleType="outline"
                                onClick={() => {
                                    trackEvent('experiment_review_canceled', { experiment_literal: params.experiment || params.exptLiteral })
                                    trackEvent('experiment_review_modal_closed', { experiment_literal: params.experiment || params.exptLiteral })
                                    setMsg('')
                                    _ref?.current?.close()
                                }}
                                data-testid="cancel-button"
                            >
                                <Icon name="close" size={1} />
                                {t('No Cancel')}
                            </Button>
                            <Button
                                theme="secondary"
                                size="xs"
                                className="text-white"
                                disabled={msg.trim() == ''}
                                onClick={() => {
                                    trackEvent('experiment_review_submitted', { experiment_literal: params.experiment || params.exptLiteral, comment_length: msg.length })
                                    postComment()
                                }}
                                data-testid="submit-button"
                            >
                                <Icon name="check" size={1} />
                                {t('Yes Submit')}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
            <form method="dialog" className="modal-backdrop">
                <button type="button">{t('Close')}</button>
            </form>
        </dialog>
    )
}

export default ExperimentCommentModal
